import { FetchError } from 'ofetch'
import type { CommonController } from '../composables/config'
import { getCookie, H3Event, deleteCookie } from 'h3'
import { useRequestEvent } from 'nuxt/app'

/**
 * A helper function to create a lazy-initialized object.
 * This can be useful when you want to create expensive resources
 * only when the subject requiring them is actually accessed.
 *
 * (For example, creating a watcher only when a property from a composable
 * is accessed.)
 * @param dest the destination object, the access to which will trigger the initialization (synchronously before the first access)
 * @param initializer the function that will be called before the first access to the destination object
 */
export function lazyInit<T extends object>(dest: T, initializer: () => any) {
    let hasBeenAccessed = false

    return new Proxy(dest, {
        get(target, prop, receiver) {
            if (!hasBeenAccessed) {
                initializer()
                hasBeenAccessed = true
            }

            return Reflect.get(target, prop, receiver)
        },
    })
}

export function isAbortError(error: any) {
    if (error instanceof FetchError) {
        return error.cause instanceof DOMException && error.cause.name === 'AbortError'
    }
    return error instanceof DOMException && error.name === 'AbortError'
}

export const getCookieByName: ((name: Parameters<CommonController['getCookie']>[0], event?: H3Event | undefined) => ReturnType<CommonController['getCookie']>) = (name, event = useRequestEvent()) => {
    // server-side
    if (event) {
        return getCookie(event, name) ?? null
    }
    // client-side
    if (!import.meta.client) return null
    return document.cookie.split(';')
        .find(row => row.trim().startsWith(`${name}=`))
        ?.split('=')[1] ?? null
}

export function deleteCookieByName(name: string, event?: H3Event | undefined, options?: {
    httpOnly?: boolean
    secure?: boolean
    domain?: string
}) {
    // server-side
    if (event) {
        deleteCookie(event, name, {
            httpOnly: options?.httpOnly,
            secure: options?.secure,
            domain: options?.domain,
        })
        return
    }
    // client-side
    if (!import.meta.client) return
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${options?.secure ? '; Secure' : ''}${options?.domain ? `; Domain=${options.domain}` : ''}`
}
